import { BaseService } from './base';

// ============================================
// Types
// ============================================

export type PostStatus = 'draft' | 'active' | 'archived';

export interface SocialPost {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  status: PostStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SocialPostProduct {
  id: string;
  post_id: string;
  feed_product_id: string;
  feed_id: string;
  sort_order: number;
  raw_data: Record<string, unknown>;
  created_at: string;
}

export type ProductSource = 'feed' | 'csv';

export interface SocialPostDesign {
  id: string;
  post_id: string;
  name: string;
  width: number;
  height: number;
  elements: SocialCanvasElement[];
  product_source: ProductSource;
  created_at: string;
  updated_at: string;
}

export interface SocialTemplate {
  id: string;
  organization_id: string | null;
  name: string;
  category: string;
  width: number;
  height: number;
  elements: SocialCanvasElement[];
  is_system: boolean;
  created_by: string | null;
  created_at: string;
}

// ── Canvas Element Types ──────────────────────

export type SocialCanvasElementType = 'text' | 'image' | 'rect' | 'circle';

interface BaseSocialElement {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  locked?: boolean;
  id?: string;
  name?: string;
  visible?: boolean;
  opacity?: number;
  shadow?: {
    x: number;
    y: number;
    blur: number;
    color: string;
  };
}

export interface SocialTextElement extends BaseSocialElement {
  type: 'text';
  content: string;
  fontSize: number;
  fontWeight: string;
  fontFamily: string;
  color: string;
  textAlign: 'left' | 'center' | 'right';
  maxWidth?: number;
  maxChars?: number;
  renderHtml?: boolean;
  letterSpacing?: number;
  lineHeight?: number;
  textShadow?: string;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
}

export interface SocialImageElement extends BaseSocialElement {
  type: 'image';
  src: string;
  objectFit: string;
  borderRadius: number;
  removeBg?: boolean;
  bgRemovedSrc?: string;
  filter?: {
    grayscale?: number;
    sepia?: number;
    brightness?: number;
    contrast?: number;
  };
}

export interface SocialRectElement extends BaseSocialElement {
  type: 'rect';
  fill: string;
  borderRadius: number;
  border: string;
}

export interface SocialCircleElement extends BaseSocialElement {
  type: 'circle';
  fill: string;
}

export type SocialCanvasElement =
  | SocialTextElement
  | SocialImageElement
  | SocialRectElement
  | SocialCircleElement;

// ── Export Job Types ──────────────────────────

export type ExportJobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'downloaded';

export interface ExportJob {
  id: string;
  status: ExportJobStatus;
  designName: string | null;
  totalCount: number;
  completedCount: number;
  failedCount: number;
  errors: Array<{ productId: string; error: string }>;
  fileSize: number | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface ExportJobStartResult {
  jobId: string;
  status: string;
  totalCount: number;
}

// ── Product input for adding ──────────────────

export interface ProductInput {
  feed_product_id: string;
  feed_id: string;
  sort_order?: number;
  raw_data: Record<string, unknown>;
}

// ============================================
// Service
// ============================================

class SocialPostingService extends BaseService {
  private readonly base = '/social-posting';

  // ── Posts ────────────────────────────────────

  async createPost(data: { name: string; description?: string }): Promise<SocialPost> {
    return this.makeRequest<SocialPost>(`${this.base}/posts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listPosts(page = 1, limit = 20): Promise<{ data: SocialPost[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const resp = await this.makeFullRequest<SocialPost[]>(`${this.base}/posts?page=${page}&limit=${limit}`);
    return {
      data: resp.data || [],
      pagination: resp.pagination || { page, limit, total: 0, pages: 0, hasNext: false, hasPrev: false },
    };
  }

  async getPost(postId: string): Promise<SocialPost> {
    return this.makeRequest<SocialPost>(`${this.base}/posts/${postId}`);
  }

  async updatePost(postId: string, data: { name?: string; description?: string; status?: PostStatus }): Promise<SocialPost> {
    return this.makeRequest<SocialPost>(`${this.base}/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePost(postId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/posts/${postId}`, { method: 'DELETE' });
  }

  // ── Products ────────────────────────────────

  async addProducts(postId: string, products: ProductInput[]): Promise<SocialPostProduct[]> {
    return this.makeRequest<SocialPostProduct[]>(`${this.base}/posts/${postId}/products`, {
      method: 'POST',
      body: JSON.stringify({ products }),
    });
  }

  async listProducts(postId: string): Promise<SocialPostProduct[]> {
    return this.makeRequest<SocialPostProduct[]>(`${this.base}/posts/${postId}/products`);
  }

  async removeProduct(postId: string, productId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/posts/${postId}/products/${productId}`, { method: 'DELETE' });
  }

  // ── Designs ─────────────────────────────────

  async createDesign(postId: string, data: { name: string; width: number; height: number; elements?: SocialCanvasElement[]; product_source?: ProductSource }): Promise<SocialPostDesign> {
    return this.makeRequest<SocialPostDesign>(`${this.base}/posts/${postId}/designs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listDesigns(postId: string): Promise<SocialPostDesign[]> {
    return this.makeRequest<SocialPostDesign[]>(`${this.base}/posts/${postId}/designs`);
  }

  async getDesign(postId: string, designId: string): Promise<SocialPostDesign> {
    return this.makeRequest<SocialPostDesign>(`${this.base}/posts/${postId}/designs/${designId}`);
  }

  async updateDesign(postId: string, designId: string, data: { name?: string; elements?: SocialCanvasElement[]; width?: number; height?: number }): Promise<SocialPostDesign> {
    return this.makeRequest<SocialPostDesign>(`${this.base}/posts/${postId}/designs/${designId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDesign(postId: string, designId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/posts/${postId}/designs/${designId}`, { method: 'DELETE' });
  }

  // ── Export ──────────────────────────────────

  async exportDesign(
    postId: string,
    designId: string,
    options: { format?: 'png' | 'jpeg'; quality?: number; scale?: number; productId?: string } = {},
  ): Promise<Blob> {
    const token = this.getAuthHeader();
    const orgId = localStorage.getItem('current_organization_id');
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

    const resp = await fetch(`${apiBase}${this.base}/posts/${postId}/designs/${designId}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: token } : {}),
        ...(orgId ? { 'x-organization-id': orgId } : {}),
      },
      body: JSON.stringify({
        format: options.format || 'png',
        quality: options.quality || 100,
        scale: options.scale || 2,
        ...(options.productId ? { productId: options.productId } : {}),
      }),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Export failed: ${text}`);
    }

    return resp.blob();
  }

  // ── Batch Export (async via BullMQ) ──────────

  async startBatchExport(
    postId: string,
    designId: string,
    options: { format?: 'png' | 'jpeg'; quality?: number; scale?: number; productIds?: string[] } = {},
  ): Promise<ExportJobStartResult> {
    return this.makeRequest<ExportJobStartResult>(`${this.base}/posts/${postId}/designs/${designId}/export-batch`, {
      method: 'POST',
      body: JSON.stringify({
        format: options.format || 'png',
        quality: options.quality || 100,
        scale: options.scale || 2,
        ...(options.productIds ? { productIds: options.productIds } : {}),
      }),
    });
  }

  async getExportJobStatus(jobId: string): Promise<ExportJob> {
    return this.makeRequest<ExportJob>(`${this.base}/export-jobs/${jobId}`);
  }

  async downloadExportJob(jobId: string): Promise<Blob> {
    const token = this.getAuthHeader();
    const orgId = localStorage.getItem('current_organization_id');
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

    const resp = await fetch(`${apiBase}${this.base}/export-jobs/${jobId}/download`, {
      headers: {
        ...(token ? { Authorization: token } : {}),
        ...(orgId ? { 'x-organization-id': orgId } : {}),
      },
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Download failed: ${text}`);
    }

    return resp.blob();
  }

  async cancelExportJob(jobId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/export-jobs/${jobId}/cancel`, { method: 'POST' });
  }

  // ── Templates ───────────────────────────────

  async listTemplates(): Promise<SocialTemplate[]> {
    return this.makeRequest<SocialTemplate[]>(`${this.base}/templates`);
  }

  async createTemplate(data: { name: string; category?: string; width: number; height: number; elements: SocialCanvasElement[] }): Promise<SocialTemplate> {
    return this.makeRequest<SocialTemplate>(`${this.base}/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await this.makeRequest<void>(`${this.base}/templates/${templateId}`, { method: 'DELETE' });
  }
}

export const socialPostingService = new SocialPostingService();
